import type { User } from 'firebase/auth'
import { AuthenticationService } from '@core/services/authentication/authentication-service'
import { UserService } from '@core/services/user/user-service'
import { removePushNotificationsToken } from '@domain/features/push-notifications/push-notifications-feature'
import type { AuthUser } from '@domain/models/auth-user'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'

const authenticationService = AuthenticationService.getInstance()
const userService = UserService.getInstance()

export class AuthFeature {
  public static getAuthUser(): AuthUser | null {
    return authenticationService.authUser
  }

  public static observeAuthSession(
    callback: (user: User | null) => void,
  ): () => void {
    return authenticationService.observeAuthState(callback)
  }

  public static async signup(email: string, password: string): Promise<User> {
    try {
      const authUser = await authenticationService.signUp(email, password)
      await userService.createUserDocument(authUser.uid, authUser.email ?? email)
      return authUser
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Sign-up failed.',
        underlyingException: error,
      })
    }
  }

  public static async login(email: string, password: string): Promise<User> {
    try {
      return await authenticationService.signIn(email, password)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Login failed.',
        underlyingException: error,
      })
    }
  }

  public static async resetPassword(email: string): Promise<void> {
    try {
      await authenticationService.resetPassword(email)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Password reset failed.',
        underlyingException: error,
      })
    }
  }

  public static async signOut(): Promise<void> {
    try {
      await authenticationService.signOut()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Sign-out failed.',
        underlyingException: error,
      })
    }
  }

  public static async signOutWithPushTokenCleanup(): Promise<void> {
    const userId = authenticationService.userId
    if (userId) {
      try {
        await removePushNotificationsToken(userId)
      } catch (error) {
        console.error('Failed to remove web push token before sign-out.', {
          userId,
          error,
        })
      }
    }

    await AuthFeature.signOut()
  }

  public static async reauthenticate(currentPassword: string): Promise<void> {
    try {
      await authenticationService.reauthenticateUser(currentPassword)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Re-authentication failed.',
        underlyingException: error,
      })
    }
  }

  public static async verifyAndUpdateEmail(
    newEmail: string,
    currentPassword: string,
  ): Promise<void> {
    try {
      await authenticationService.verifyAndUpdateEmail(newEmail, currentPassword)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Verify and update email failed.',
        underlyingException: error,
      })
    }
  }

  public static async deleteUser(): Promise<void> {
    try {
      await authenticationService.deleteUser()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Account deletion failed.',
        underlyingException: error,
      })
    }
  }

  public static async deleteAccount(): Promise<void> {
    await AuthFeature.deleteUser()
  }
}

export const observeAuthSession = (callback: (user: User | null) => void) =>
  AuthFeature.observeAuthSession(callback)

export async function signUp(email: string, password: string): Promise<User> {
  return AuthFeature.signup(email, password)
}

export async function login(email: string, password: string): Promise<User> {
  return AuthFeature.login(email, password)
}

export async function resetPassword(email: string): Promise<void> {
  return AuthFeature.resetPassword(email)
}

export async function logout(): Promise<void> {
  return AuthFeature.signOut()
}

export async function logoutWithPushTokenCleanup(): Promise<void> {
  return AuthFeature.signOutWithPushTokenCleanup()
}

export async function reauthenticateUser(currentPassword: string): Promise<void> {
  return AuthFeature.reauthenticate(currentPassword)
}

export async function verifyAndUpdateEmail(
  newEmail: string,
  currentPassword: string,
): Promise<void> {
  return AuthFeature.verifyAndUpdateEmail(newEmail, currentPassword)
}

export async function deleteAccount(): Promise<void> {
  return AuthFeature.deleteAccount()
}

export async function deleteUser(): Promise<void> {
  return AuthFeature.deleteUser()
}
